import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  contextoDoTipo,
  templates,
  tiposDeDocumento,
  type Template,
  type TemplateFormat,
  type TemplateKind,
  type TemplateStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import { AvisoDoLaudo } from './AvisoDoLaudo';
import { EditorDeCorpo } from './EditorDeCorpo';
import { PERMISSAO_VER_VINCULOS, TemplateBindingsTab } from './TemplateBindingsTab';
import { TemplateDocxPanel } from './TemplateDocxPanel';
import { PERMISSAO_VER_CARGAS, TextLoadsTab } from './TextLoadsTab';
import { useToast } from '@/shared/ui/useToast';

/**
 * Modelos versionados de documento e mensagem.
 *
 * O corpo nao pertence ao template, e sim a uma versao dele. Editar o texto
 * publica a proxima versao e a anterior permanece, porque e ela que explica o
 * documento que ja foi gerado.
 */

const limite = 25;

const tipos: { value: TemplateKind | ''; label: string }[] = [
  { value: '', label: 'Todos os tipos' },
  { value: 'minuta', label: 'Minuta' },
  { value: 'ccg', label: 'CCG da operacao' },
  { value: 'ccg_tomador', label: 'CCG do tomador' },
  { value: 'apolice', label: 'Apolice' },
  { value: 'laudo_emissao', label: 'Laudo de emissao' },
  { value: 'email', label: 'E-mail' },
  { value: 'whatsapp', label: 'Mensagem' },
];

const tiposParaCriar = tipos.filter((tipo) => tipo.value !== '');

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
];

const rotuloDoTipo: Record<TemplateKind, string> = {
  minuta: 'Minuta',
  ccg: 'CCG da operacao',
  ccg_tomador: 'CCG do tomador',
  apolice: 'Apolice',
  laudo_emissao: 'Laudo de emissao',
  email: 'E-mail',
  whatsapp: 'Mensagem',
};

/** Chave em formato estavel a partir do nome digitado. */
function chaveSugerida(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function TemplatesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [tipo, setTipo] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<Template | null>(null);
  const [historico, setHistorico] = useState<Template | null>(null);
  // Abas: os modelos, os vinculos do documento da apolice (P-MIN-2) e a carga dos
  // textos da seguradora (P-MIN-5).
  const [aba, setAba] = useState<'modelos' | 'vinculos' | 'cargas'>('modelos');
  const veVinculos = can(PERMISSAO_VER_VINCULOS);
  const veCargas = can(PERMISSAO_VER_CARGAS);

  const podeCriar = can('templates.template.create');
  const podeEditar = can('templates.template.update');
  const podeExcluir = can('templates.template.delete');

  const consulta = useQuery({
    queryKey: ['templates', { tipo, status, offset }],
    queryFn: ({ signal }) =>
      templates.list({ kind: tipo, status, limit: limite, offset }, signal),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => templates.remove(id),
    onSuccess: () => {
      toast.success('Template excluido.');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const colunas: Column<Template>[] = [
    {
      key: 'nome',
      header: 'Template',
      render: (template) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{template.name}</p>
          <p className="truncate font-mono text-2xs text-ink-500">{template.key}</p>
        </div>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (template) => (
        <Badge tone={tiposDeDocumento.includes(template.kind) ? 'brand' : 'info'}>
          {rotuloDoTipo[template.kind]}
        </Badge>
      ),
    },
    {
      key: 'categoria',
      header: 'Categoria',
      hideBelow: 'lg',
      render: (template) => template.category || '--',
    },
    {
      key: 'versao',
      header: 'Versao',
      align: 'right',
      render: (template) =>
        template.current_version > 0 ? (
          <span className="tabular-nums">v{template.current_version}</span>
        ) : (
          <span className="text-2xs text-ink-500">Sem versao publicada</span>
        ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (template) => (
        <Badge tone={template.status === 'ativo' ? 'success' : 'neutral'}>
          {template.status === 'ativo' ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (template) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setHistorico(template)}>
            Historico
          </Button>
          {podeEditar ? (
            <Button variant="secondary" onClick={() => setEditando(template)}>
              Editar
            </Button>
          ) : null}
          {podeExcluir && template.current_version === 0 ? (
            <Button
              variant="danger"
              loading={excluir.isPending}
              onClick={() => excluir.mutate(template.id)}
            >
              Excluir
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Templates"
        description="Modelos de minuta, CCG, apolice, laudo e mensagem, com historico de versoes."
        actions={
          podeCriar ? (
            <Button onClick={() => setCriando(true)}>Novo template</Button>
          ) : null
        }
      />

      {veVinculos || veCargas ? (
        <div
          role="tablist"
          aria-label="Abas de templates"
          className="flex gap-1 border-b border-line"
        >
          {(
            [
              { chave: 'modelos', rotulo: 'Modelos', ve: true },
              { chave: 'vinculos', rotulo: 'Vinculos', ve: veVinculos },
              { chave: 'cargas', rotulo: 'Carga de textos', ve: veCargas },
            ] as const
          )
            .filter((item) => item.ve)
            .map((item) => (
              <button
                key={item.chave}
                type="button"
                role="tab"
                aria-selected={aba === item.chave}
                onClick={() => setAba(item.chave)}
                className={
                  aba === item.chave
                    ? 'border-b-2 border-brand-500 px-3 py-2 text-xs font-semibold text-ink-900'
                    : 'border-b-2 border-transparent px-3 py-2 text-xs text-ink-500 hover:text-ink-900'
                }
              >
                {item.rotulo}
              </button>
            ))}
        </div>
      ) : null}

      {aba === 'vinculos' ? <TemplateBindingsTab /> : null}
      {aba === 'cargas' ? <TextLoadsTab /> : null}

      {aba === 'modelos' && consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os templates">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card className={aba === 'modelos' ? '' : 'hidden'}>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Tipo"
            className="h-9"
            options={tipos}
            value={tipo}
            onChange={(event) => {
              setTipo(event.target.value);
              setOffset(0);
            }}
          />
          <Select
            label="Situacao"
            className="h-9"
            options={situacoes}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(template) => template.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="copy"
              title="Nenhum template cadastrado"
              description="O template guarda o texto usado na minuta, na CCG e nas mensagens da esteira. Cada edicao publica uma versao nova, e a anterior continua explicando o que ja foi gerado."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {criando ? <TemplateFormDialog open onClose={() => setCriando(false)} /> : null}

      {editando ? (
        <TemplateEditDialog
          template={editando}
          open
          onClose={() => setEditando(null)}
        />
      ) : null}

      {historico ? (
        <TemplateHistoryDialog
          template={historico}
          open
          onClose={() => setHistorico(null)}
        />
      ) : null}
    </div>
  );
}

function TemplateFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const [tipo, setTipo] = useState<TemplateKind>('minuta');
  const [formato, setFormato] = useState<TemplateFormat>('texto');
  const [categoria, setCategoria] = useState('');
  const [corpo, setCorpo] = useState('');

  // Arquivo do Word produz documento. E-mail e mensagem saem como texto e nao tem
  // para onde levar um documento formatado, entao a escolha some para eles.
  const aceitaWord = tiposDeDocumento.includes(tipo);
  // A CCG do tomador vai ao provedor de assinatura, que precisa de arquivo: o modelo
  // dela e sempre em Word.
  const soWord = tipo === 'ccg_tomador';
  const formatoEfetivo: TemplateFormat = soWord
    ? 'docx'
    : aceitaWord
      ? formato
      : 'texto';

  const criar = useMutation({
    mutationFn: () =>
      templates.create({
        key: chave || chaveSugerida(nome),
        name: nome,
        kind: tipo,
        format: formatoEfetivo,
        category: categoria,
        body: formatoEfetivo === 'docx' ? '' : corpo,
      }),
    onSuccess: () => {
      toast.success('Template criado.');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo template"
      description="Com corpo preenchido, o template ja nasce com a primeira versao publicada."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={criar.isPending}
            disabled={nome.trim() === ''}
            onClick={() => criar.mutate()}
          >
            Criar template
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Nome"
            required
            value={nome}
            onChange={(event) => setNome(event.target.value)}
          />
          <Select
            label="Tipo"
            options={tiposParaCriar}
            value={tipo}
            onChange={(event) => setTipo(event.target.value as TemplateKind)}
          />
          <Field
            label="Categoria"
            value={categoria}
            onChange={(event) => setCategoria(event.target.value)}
          />
        </div>

        <Field
          label="Chave"
          value={chave || chaveSugerida(nome)}
          hint="Referencia estavel do modelo. Nao muda depois de criada."
          onChange={(event) => setChave(event.target.value)}
        />

        {tipo === 'laudo_emissao' ? <AvisoDoLaudo /> : null}

        {tipo === 'ccg_tomador' ? (
          <Alert tone="info" title="CCG do tomador">
            O modelo do contrato de CCG do tomador e sempre em Word e so aceita o que o
            contrato preenche: o tomador, o contrato, os representantes e os fiadores. A
            operacao e o segurado ficam no modelo da CCG da operacao.
          </Alert>
        ) : null}

        {aceitaWord && !soWord ? (
          <Select
            label="Formato"
            value={formato}
            hint={
              formato === 'docx'
                ? 'O contrato vem de um .docx ja formatado. O arquivo e importado depois de criar o modelo.'
                : 'O corpo e escrito aqui. Serve quando o documento nao precisa de margem, cabecalho nem tabela.'
            }
            options={[
              { value: 'texto', label: 'Texto escrito aqui' },
              { value: 'docx', label: 'Documento do Word (.docx)' },
            ]}
            onChange={(event) => setFormato(event.target.value as TemplateFormat)}
          />
        ) : null}

        {formatoEfetivo === 'docx' ? (
          <Alert tone="info" title="O documento vem depois">
            O modelo nasce vazio. Assim que ele existir, importe o .docx ja formatado
            pela tela de edicao: as margens, o cabecalho, o rodape, as tabelas e as
            imagens vem do proprio arquivo.
          </Alert>
        ) : (
          <EditorDeCorpo
            label="Corpo"
            value={corpo}
            onChange={setCorpo}
            contexto={contextoDoTipo[tipo]}
            hint="Clique em uma variavel para inseri-la onde o cursor esta. A lista e a do tipo escolhido: uma variavel fora dela e recusada na publicacao."
          />
        )}
      </div>
    </Modal>
  );
}

function TemplateEditDialog({
  template,
  open,
  onClose,
}: {
  template: Template;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [nome, setNome] = useState(template.name);
  const [categoria, setCategoria] = useState(template.category);
  const [status, setStatus] = useState<TemplateStatus>(template.status);
  const [corpo, setCorpo] = useState('');
  const [notas, setNotas] = useState('');

  const versaoAtual = useQuery({
    queryKey: ['template-history', template.id],
    queryFn: ({ signal }) => templates.history(template.id, signal),
    enabled: template.current_version > 0,
  });

  const salvar = useMutation({
    mutationFn: () =>
      templates.update(template.id, {
        name: nome,
        category: categoria,
        status,
      }),
    onSuccess: () => {
      toast.success('Template atualizado.');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const publicar = useMutation({
    mutationFn: () => templates.publish(template.id, corpo, notas),
    onSuccess: (versao) => {
      toast.success(`Versao ${versao.version} publicada.`);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      void queryClient.invalidateQueries({ queryKey: ['template-history'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const corpoAtual = versaoAtual.data?.[0]?.body ?? '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Editar ${template.name}`}
      description="Publicar o corpo cria a proxima versao. A anterior continua explicando o que ja foi gerado."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="secondary"
            loading={salvar.isPending}
            disabled={nome.trim() === ''}
            onClick={() => salvar.mutate()}
          >
            Salvar dados
          </Button>
          {template.format === 'docx' ? null : (
            <Button
              loading={publicar.isPending}
              disabled={corpo.trim() === ''}
              onClick={() => publicar.mutate()}
            >
              Publicar versao
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Nome"
            required
            value={nome}
            onChange={(event) => setNome(event.target.value)}
          />
          <Field
            label="Categoria"
            value={categoria}
            onChange={(event) => setCategoria(event.target.value)}
          />
          <Select
            label="Situacao"
            options={situacoes.filter((situacao) => situacao.value !== '')}
            value={status}
            onChange={(event) => setStatus(event.target.value as TemplateStatus)}
          />
        </div>

        <div className="rounded-md border border-line bg-ink-50/50 px-3 py-2">
          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
            Chave
          </p>
          <p className="font-mono text-xs text-ink-700">{template.key}</p>
        </div>

        {template.kind === 'laudo_emissao' ? <AvisoDoLaudo /> : null}

        {template.format === 'docx' ? (
          <TemplateDocxPanel
            template={template}
            versaoAtual={versaoAtual.data?.[0]}
            onPublicado={onClose}
          />
        ) : (
          <>
            <EditorDeCorpo
              label="Corpo da proxima versao"
              value={corpo}
              onChange={setCorpo}
              contexto={contextoDoTipo[template.kind]}
              hint={
                corpoAtual
                  ? 'Clique em uma variavel para inseri-la onde o cursor esta. Comece do texto atual usando o botao abaixo, ou escreva do zero.'
                  : 'O template ainda nao tem versao publicada.'
              }
            />

            {corpoAtual && corpo === '' ? (
              <div>
                <Button variant="secondary" onClick={() => setCorpo(corpoAtual)}>
                  Partir do texto atual
                </Button>
              </div>
            ) : null}
          </>
        )}

        {template.format === 'docx' ? null : (
          <Field
            label="Nota da versao"
            value={notas}
            hint="O que mudou e por que. Sem isso, so a comparacao linha a linha explica."
            onChange={(event) => setNotas(event.target.value)}
          />
        )}
      </div>
    </Modal>
  );
}

function TemplateHistoryDialog({
  template,
  open,
  onClose,
}: {
  template: Template;
  open: boolean;
  onClose: () => void;
}) {
  const consulta = useQuery({
    queryKey: ['template-history', template.id],
    queryFn: ({ signal }) => templates.history(template.id, signal),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Historico de ${template.name}`}
      description="Da versao mais recente para a mais antiga."
      size="lg"
      footer={<Button onClick={onClose}>Fechar</Button>}
    >
      {consulta.isPending ? (
        <p className="text-sm text-ink-500">Carregando o historico.</p>
      ) : (consulta.data ?? []).length === 0 ? (
        <EmptyState
          icon="copy"
          title="Nenhuma versao publicada"
          description="O template ainda esta como rascunho e nao gera documento."
        />
      ) : (
        <ol className="flex flex-col gap-4">
          {(consulta.data ?? []).map((versao) => (
            <li key={versao.id} className="rounded-md border border-line p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge
                  tone={
                    versao.version === template.current_version ? 'success' : 'neutral'
                  }
                >
                  {versao.version === template.current_version
                    ? `v${versao.version} em uso`
                    : `v${versao.version}`}
                </Badge>
                <span className="text-2xs text-ink-500">
                  {versao.published_label} em {formatDate(versao.published_at)}
                </span>
              </div>

              {versao.notes ? (
                <p className="mt-2 text-xs text-ink-600">{versao.notes}</p>
              ) : null}

              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-ink-50 p-2 font-mono text-2xs text-ink-700">
                {versao.body}
              </pre>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
